import React, { useEffect, useState } from "react";
import {
  CircularProgress, Button, Box, Typography, Avatar, Paper,
  List, ListItem, ListItemIcon, ListItemText,
} from "@mui/material";
import SchoolIcon      from "@mui/icons-material/School";
import PeopleIcon      from "@mui/icons-material/People";
import PersonAddIcon   from "@mui/icons-material/PersonAdd";
import SettingsIcon    from "@mui/icons-material/Settings";
import ExitToAppIcon   from "@mui/icons-material/ExitToApp";
import TrendingUpIcon  from "@mui/icons-material/TrendingUp";
import WarningIcon     from "@mui/icons-material/Warning";
import EventSeatIcon   from "@mui/icons-material/EventSeat";
import PersonIcon      from "@mui/icons-material/Person";
import AutorenewIcon   from "@mui/icons-material/Autorenew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useSnackbar } from "notistack";
import { useNavigate, useParams } from "react-router-dom";
import { get_school_api } from "../../DAL/FlightSchools/FlightSchools";
import { baseUrl } from "../../config/config";

const cardSx = {
  p: 3,
  backgroundColor: "#ffffff",
  border: "1px solid rgba(26,26,26,0.09)",
  borderRadius: "12px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
};

const StatCard = ({ label, value, subValue, icon, color = "#c01e2e" }) => (
  <Paper sx={{ ...cardSx, display: "flex", alignItems: "center", gap: 2, height: "100%" }}>
    <Box sx={{ width: 52, height: 52, borderRadius: "12px", backgroundColor: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {React.cloneElement(icon, { sx: { color, fontSize: 26 } })}
    </Box>
    <Box>
      <Typography sx={{ color: "#aaa", fontSize: "11px", mb: 0.5, fontFamily: "'Rubik', sans-serif", textTransform: "uppercase", letterSpacing: "0.3px" }}>{label}</Typography>
      <Typography sx={{ color: "#1a1a1a", fontSize: "26px", fontWeight: 700, lineHeight: 1, fontFamily: "'Rubik', sans-serif" }}>{value}</Typography>
      {subValue && <Typography sx={{ color: "#aaa", fontSize: "11px", mt: 0.5, fontFamily: "'Rubik', sans-serif" }}>{subValue}</Typography>}
    </Box>
  </Paper>
);

const QuickActionButton = ({ label, icon, onClick, variant = "outlined" }) => (
  <Button variant={variant} startIcon={icon} onClick={onClick} fullWidth
    sx={{
      py: 1.5,
      textTransform: "none",
      fontFamily: "'Rubik', sans-serif",
      borderRadius: "8px",
      justifyContent: "flex-start",
      boxShadow: "none",
      borderColor: variant === "outlined" ? "rgba(26,26,26,0.2)" : undefined,
      color: variant === "outlined" ? "#1a1a1a" : "#fff",
      backgroundColor: variant === "contained" ? "#c01e2e" : undefined,
      "&:hover": {
        borderColor: variant === "outlined" ? "#c01e2e" : undefined,
        backgroundColor: variant === "contained" ? "#a81828" : "rgba(192,30,46,0.05)",
        boxShadow: "none",
      },
    }}
  >
    {label}
  </Button>
);

const ActivityItem = ({ icon, text, time, color = "#c01e2e" }) => (
  <ListItem sx={{ px: 0, py: 1.25, borderBottom: "1px solid rgba(26,26,26,0.06)" }}>
    <ListItemIcon sx={{ minWidth: 40 }}>
      <Box sx={{ width: 30, height: 30, borderRadius: "8px", backgroundColor: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {React.cloneElement(icon, { sx: { color, fontSize: 15 } })}
      </Box>
    </ListItemIcon>
    <ListItemText
      primary={text} secondary={time}
      primaryTypographyProps={{ sx: { color: "#1a1a1a", fontSize: "13px", fontFamily: "'Rubik', sans-serif" } }}
      secondaryTypographyProps={{ sx: { color: "#aaa", fontSize: "11px", fontFamily: "'Rubik', sans-serif" } }}
    />
  </ListItem>
);

export default function FlightSchoolAdminDashboard() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate   = useNavigate();
  const { school_id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [school,    setSchool]    = useState(null);

  useEffect(() => { loadSchoolData(); }, [school_id]);

  const loadSchoolData = async () => {
    try {
      const impersonated = localStorage.getItem("impersonating_school");
      const targetId = school_id || (impersonated ? JSON.parse(impersonated)._id : null);
      if (targetId) {
        const r = await get_school_api(targetId);
        if (r.code === 200) { setSchool(r.school); setIsLoading(false); }
        else { enqueueSnackbar(r.message || "Failed to load school", { variant: "error" }); navigate("/flight-schools"); }
      } else { enqueueSnackbar("No school selected", { variant: "error" }); navigate("/flight-schools"); }
    } catch { enqueueSnackbar("Error loading school data", { variant: "error" }); navigate("/flight-schools"); }
  };

  if (isLoading) return <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}><CircularProgress sx={{ color: "#c01e2e" }} /></Box>;

  const totalStudents = school?.total_students_count || school?.active_students_count || 0;
  const activeStudents = school?.active_students_count || 0;
  const expiringSoon   = school?.expiring_soon_count   || 0;
  const totalSeats     = school?.included_seats        || 0;
  const usedSeats      = activeStudents;

  const recentActivities = [
    { icon: <PersonAddIcon />,   text: "New student John Doe joined",                    time: "2 hours ago",  color: "#16a34a" },
    { icon: <AutorenewIcon />,   text: "Subscription renewed for Jane Smith",             time: "5 hours ago",  color: "#2563eb" },
    { icon: <CheckCircleIcon />, text: "Student Mike Johnson completed onboarding",       time: "1 day ago",    color: "#16a34a" },
    { icon: <PersonAddIcon />,   text: "New student Sarah Williams joined",               time: "2 days ago",   color: "#16a34a" },
    { icon: <WarningIcon />,     text: "3 student subscriptions expiring soon",           time: "3 days ago",   color: "#d97706" },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 3, mb: 4, borderBottom: "1px solid rgba(26,26,26,0.09)" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar src={school.branding?.logo ? baseUrl + school.branding.logo : undefined}
            sx={{ width: 52, height: 52, backgroundColor: school.branding?.primary_color || "#c01e2e", border: "2px solid rgba(26,26,26,0.12)" }}>
            <SchoolIcon sx={{ fontSize: 26 }} />
          </Avatar>
          <Box>
            <Typography sx={{ color: "#1a1a1a", fontSize: "20px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>{school.name}</Typography>
            <Typography sx={{ color: "#aaa", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>Admin Dashboard</Typography>
          </Box>
        </Box>
        <Button variant="outlined" startIcon={<ExitToAppIcon />}
          onClick={() => { localStorage.removeItem("impersonating_school"); enqueueSnackbar("Exited God Mode", { variant: "info" }); navigate(`/flight-schools/${school_id || school?._id}`); }}
          sx={{ borderColor: "#c01e2e", color: "#c01e2e", textTransform: "none", fontFamily: "'Rubik', sans-serif", borderRadius: "8px",
            "&:hover": { backgroundColor: "rgba(192,30,46,0.06)", borderColor: "#c01e2e" } }}>
          Exit God Mode
        </Button>
      </Box>

      {/* Stats */}
      <Typography sx={{ color: "#1a1a1a", fontSize: "15px", fontWeight: 600, fontFamily: "'Rubik', sans-serif", mb: 2 }}>Analytics Overview</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 2, mb: 4 }}>
        <StatCard label="Total Students"  value={totalStudents}                     icon={<PeopleIcon />}    />
        <StatCard label="Active Students" value={activeStudents}                     icon={<PersonIcon />}    color="#16a34a" />
        <StatCard label="Expiring Soon"   value={expiringSoon}   subValue="Next 30 days" icon={<WarningIcon />}   color="#d97706" />
        <StatCard label="Seats Used"      value={`${usedSeats}/${totalSeats}`}       icon={<EventSeatIcon />} color={usedSeats > totalSeats ? "#d97706" : "#2563eb"}
          subValue={usedSeats > totalSeats ? `${usedSeats - totalSeats} extra seats` : "Within limit"} />
      </Box>

      {/* Chart placeholders */}
      <Typography sx={{ color: "#1a1a1a", fontSize: "15px", fontWeight: 600, fontFamily: "'Rubik', sans-serif", mb: 2 }}>Usage Analytics</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 4 }}>
        {[{ icon: <TrendingUpIcon />, label: "Usage Trends" }, { icon: <PeopleIcon />, label: "Student Activity" }].map(({ icon, label }) => (
          <Paper key={label} sx={{ ...cardSx, height: 180, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
            {React.cloneElement(icon, { sx: { color: "#ddd", fontSize: 40 } })}
            <Typography sx={{ color: "#aaa", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>{label}</Typography>
            <Typography sx={{ color: "#ccc", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>Chart coming soon</Typography>
          </Paper>
        ))}
      </Box>

      {/* Quick Actions + Recent Activity */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" }, gap: 3 }}>
        <Paper sx={cardSx}>
          <Typography sx={{ color: "#1a1a1a", fontSize: "15px", fontWeight: 600, fontFamily: "'Rubik', sans-serif", mb: 2 }}>Quick Actions</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <QuickActionButton label="Add New Student"  icon={<PersonAddIcon />}  onClick={() => navigate(`/flight-schools/${school_id || school?._id}?tab=students&action=add`)} variant="contained" />
            <QuickActionButton label="View All Students" icon={<PeopleIcon />}    onClick={() => navigate(`/flight-schools/${school_id || school?._id}?tab=students`)} />
            <QuickActionButton label="Manage Settings"  icon={<SettingsIcon />}   onClick={() => navigate(`/flight-schools/${school_id || school?._id}?tab=settings`)} />
          </Box>
        </Paper>
        <Paper sx={cardSx}>
          <Typography sx={{ color: "#1a1a1a", fontSize: "15px", fontWeight: 600, fontFamily: "'Rubik', sans-serif", mb: 1 }}>Recent Activity</Typography>
          <List sx={{ p: 0 }}>
            {recentActivities.map((a, i) => <ActivityItem key={i} icon={a.icon} text={a.text} time={a.time} color={a.color} />)}
          </List>
        </Paper>
      </Box>
    </Box>
  );
}
